<template>
  <div :class="darkModeClass">
    <div class="tab-group">
      <slot>
        <Heading v-if="panel.showTitle" :level="1" v-text="panel.name" />

        <p
          v-if="panel.helpText"
          :class="panel.helpText ? 'tabs-mt-2' : 'tabs-mt-3'"
          class="tabs-text-gray-500 tabs-text-sm tabs-font-semibold tabs-italic"
          v-html="panel.helpText"
        ></p>
      </slot>

      <div
        class="bg-white dark:bg-gray-800 rounded-b-xl rounded-t-xl overflow-hidden"
        :class="[panel.showTitle && !panel.showToolbar ? 'tabs-mt-3' : '']"
      >
        <div id="tabs" class="overflow-hidden">
          <div class="block overflow-x-auto overflow-y-hidden">
            <nav
              aria-label="Tabs"
              class="isolate flex divide-x divide-gray-200 dark:divide-gray-700 rounded-t-xl nova-tabs"
            >
              <a
                v-for="(tab, key) in getSortedTabs(tabs)"
                :key="key"
                :class="
                  [
                    getIsTabCurrent(tab)
                    ? 'active text-' + getCurrentColor() + '-500 font-bold'
                    : 'font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
                    {'rounded-tl-2xl': key === 0},
                    {'rounded-tr-2xl': key === getSortedTabs(tabs).length - 1},
                  ]
                "
                :dusk="tab.slug + '-tab'"
                :ref="tab.slug + '-tab'"
                class="block relative flex-none overflow-hidden bg-white dark:bg-gray-800 py-4 px-4 text-center text-sm leading-snug whitespace-normal break-words min-h-[3.5rem] min-w-[33%] focus:z-10 cursor-pointer tab-item"
                :style="getTabWidth()"
                @click.prevent="handleTabClick(tab)"
              >
                <span class="capitalize block">{{ tab.properties.title }}</span>
                <span
                  aria-hidden="true"
                  class="absolute inset-x-0 bottom-0 h-0.5"
                  :class="[
                    getIsTabCurrent(tab)
                      ? 'bg-' + getCurrentColor() + '-500'
                      : 'bg-gray-200 dark:bg-gray-700',
                  ]"
                ></span>
              </a>
            </nav>
          </div>
        </div>

        <div
          v-for="(tab, index) in getSortedTabs(tabs)"
          v-show="getIsTabCurrent(tab)"
          :key="'related-tabs-fields' + index"
          :ref="getTabRefName(tab)"
          :class="['tab', getIsTabCurrent(tab) ? 'block' : 'hidden', tab.slug]"
          :label="tab.name"
        >
          <div
            class="bg-white dark:bg-gray-800 rounded-b-2xl w-full flex flex-wrap divide-y divide-gray-100 dark:divide-gray-700"
          >
            <KeepAlive>
              <template
                v-for="(field, index) in tab.fields"
                :key="'tab-' + index"
              >
                <component
                  v-if="!field.from"
                  :is="getComponentName(field)"
                  ref="fields"
                  :class="{
                    'remove-bottom-border': index === tab.fields.length - 1,
                  }"
                  :errors="validationErrors"
                  :field="field"
                  :form-unique-id="formUniqueId"
                  :related-resource-id="relatedResourceId"
                  :related-resource-name="relatedResourceName"
                  :resource-id="resourceId"
                  :resource-name="resourceName"
                  :show-help-text="field.helpText != null"
                  :shown-via-new-relation-modal="shownViaNewRelationModal"
                  :via-relationship="viaRelationship"
                  :via-resource="viaResource"
                  :via-resource-id="viaResourceId"
                  @field-changed="$emit('field-changed')"
                  @file-deleted="$emit('update-last-retrieved-at-timestamp')"
                  @file-upload-started="$emit('file-upload-started')"
                  @file-upload-finished="$emit('file-upload-finished')"
                />

                <component
                  v-if="field.from"
                  :is="getComponentName(field)"
                  :errors="validationErrors"
                  :resource-id="getResourceId(field)"
                  :resource-name="field.resourceName || resourceName"
                  :field="field"
                  :via-resource="field.from.viaResource"
                  :via-resource-id="field.from.viaResourceId"
                  :via-relationship="field.from.viaRelationship"
                  :form-unique-id="relationFormUniqueId"
                  @field-changed="$emit('field-changed')"
                  @file-deleted="$emit('update-last-retrieved-at-timestamp')"
                  @file-upload-started="$emit('file-upload-started')"
                  @file-upload-finished="$emit('file-upload-finished')"
                  :show-help-text="field.helpText != null"
                />
              </template>
            </KeepAlive>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BehavesAsPanel from '../mixins/BehavesAsPanel'
import HasTabs from '../mixins/HasTabs'

export default {
  mixins: [BehavesAsPanel, HasTabs],
  data: () => ({
    tabMode: 'form',
  }),
  methods: {
    getAllFieldsExceptRelatable(tab) {
      return tab.fields.filter(field => !field.relatable)
    },
    getAllRelatableFields(tab) {
      return tab.fields.filter(field => field.relatable)
    },
    getTabWidth() {
      const tabsCount = this.getSortedTabs(this.tabs).length
      const minTabsForScroll = 5
      
      if (tabsCount < minTabsForScroll) {
        // اگر کمتر از 5 تا تب بود، عرض را بر اساس تعداد واقعی محاسبه کن
        const widthPercent = 100 / tabsCount
        return {
          '--tab-width-desktop': `${widthPercent}%`
        }
      }
      
      // اگر 5 تا یا بیشتر بود، حداقل 20% (برای اسکرول)
      return {
        '--tab-width-desktop': '20%'
      }
    },
  },
}
</script>

<style scoped>
@media (min-width: 640px) {
  .tab-item {
    min-width: var(--tab-width-desktop, 20%);
  }
}
</style>
